import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import type { Me } from '../api'
import { api, ApiError } from '../api'

/** Login or registration; after success the session (and an anonymous cart) belongs to the user. */
export function AuthForm({ initial = 'login', onDone }: { initial?: 'login' | 'register'; onDone?: (me: Me) => void }) {
  const [mode, setMode] = useState(initial)
  const [f, setF] = useState({ username: '', password: '', first_name: '', last_name: '', organisation: '' })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const qc = useQueryClient()
  const first = useRef<HTMLInputElement>(null)
  useEffect(() => { first.current?.focus() }, [mode])
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value })

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.csrf()
      const me = mode === 'login'
        ? await api.login(f.username, f.password)
        : await api.register({ email: f.username, password: f.password, first_name: f.first_name, last_name: f.last_name, organisation: f.organisation })
      await api.csrf()           // the session changed, so did the CSRF token
      qc.setQueryData(['me'], me)
      qc.invalidateQueries({ queryKey: ['search'] })
      qc.invalidateQueries({ queryKey: ['cart'] })
      onDone?.(me)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <div className="seg auth-tabs" role="tablist">
        <button role="tab" aria-selected={mode === 'login'} aria-pressed={mode === 'login'} onClick={() => setMode('login')} type="button">Anmelden</button>
        <button role="tab" aria-selected={mode === 'register'} aria-pressed={mode === 'register'} onClick={() => setMode('register')} type="button">Neues Konto</button>
      </div>
      <form onSubmit={submit}>
        {mode === 'register' && (
          <div className="fgrid">
            <label>Vorname<input ref={first} value={f.first_name} onChange={set('first_name')} autoComplete="given-name" /></label>
            <label>Nachname<input value={f.last_name} onChange={set('last_name')} autoComplete="family-name" /></label>
            <label className="span2">Organisation / Verlag (optional)<input value={f.organisation} onChange={set('organisation')} autoComplete="organization" /></label>
          </div>
        )}
        <label>{mode === 'login' ? 'E-Mail oder Benutzername' : 'E-Mail'}
          <input ref={mode === 'login' ? first : undefined} type={mode === 'login' ? 'text' : 'email'} value={f.username}
            onChange={set('username')} autoComplete={mode === 'login' ? 'username' : 'email'} required /></label>
        <label>Passwort
          <input type="password" value={f.password} onChange={set('password')} required minLength={mode === 'register' ? 8 : undefined}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'} /></label>
        {mode === 'register' && <span className="note">Mindestens 8 Zeichen, nicht nur Ziffern.</span>}
        {error && <div className="err" role="alert">{error}</div>}
        <div className="row2">
          <span className="note">{mode === 'login' ? 'Recherche-Zugang vergibt die Stiftung auf Anfrage.' : 'Für Bestellungen und Downloads.'}</span>
          <button className="btn primary" type="submit" disabled={busy}>{busy ? 'Moment …' : mode === 'login' ? 'Anmelden' : 'Konto anlegen'}</button>
        </div>
      </form>
    </div>
  )
}
